import { inject, Injectable, Provider } from '@angular/core'
import {
  ActivatedRouteSnapshot,
  DetachedRouteHandle,
  Route,
  RouteReuseStrategy,
} from '@angular/router'

import { DeviceService } from '../services/device.service'

@Injectable()
export class CustomRouteReuseStrategy implements RouteReuseStrategy {
  protected readonly cache = new Map<
    Route,
    {
      handle: DetachedRouteHandle
      route: ActivatedRouteSnapshot
      timestamp: number
    }
  >()

  protected readonly device = inject(DeviceService)
  protected readonly MAX_CACHED_ROUTES = this.device.isMobilePlatform ? 3 : 5

  public shouldDetach(route: ActivatedRouteSnapshot): boolean {
    // This ensures all disposable popups are closed after route is changed
    setTimeout(() => document.body.click(), 128)

    // A route opts out of reuse with `data: { reuseRoute: false }`. Parameterized detail/edit
    // screens rebuild themselves from their route params, so serving a cached view means
    // serving state that belongs to a previously opened entity.
    return !!route && route.routeConfig?.data?.['reuseRoute'] !== false
  }

  /**
   * The router calls `store(route, null)` right after a stored handle is attached, to release
   * its ownership of that handle. Such an entry must be removed instead of being kept with an
   * empty handle: `shouldAttach()` would keep reporting a cached route while `retrieve()`
   * returns `null`, and the router dereferences the retrieved handle without a null check.
   */
  public store(route: ActivatedRouteSnapshot, handle: DetachedRouteHandle | null): void {
    if (!route.routeConfig) return

    if (!handle) {
      this.cache.delete(route.routeConfig)
      return
    }

    this.cache.set(route.routeConfig, { handle, route, timestamp: Date.now() })
    this.cleanupCachedRoutes()
  }

  public shouldAttach(route: ActivatedRouteSnapshot): boolean {
    return !!route.routeConfig && !!this.cache.get(route.routeConfig)?.handle
  }

  public retrieve(route: ActivatedRouteSnapshot): DetachedRouteHandle | null {
    if (!route.routeConfig || !this.cache.has(route.routeConfig)) {
      return null
    }
    return this.cache.get(route.routeConfig)?.handle || null
  }

  public shouldReuseRoute(future: ActivatedRouteSnapshot, curr: ActivatedRouteSnapshot): boolean {
    return future.routeConfig === curr.routeConfig
  }

  protected cleanupCachedRoutes() {
    if (this.cache.size > this.MAX_CACHED_ROUTES) {
      const minCachedTimestamp = Math.min(
        ...Array.from(this.cache.values()).map((entry) => entry.timestamp),
      )
      const cacheEntryToRemove = Array.from(this.cache.entries()).find(
        ([, entry]) => entry.timestamp === minCachedTimestamp,
      )
      if (cacheEntryToRemove) {
        this.cache.delete(cacheEntryToRemove[0])

        // Properly destroy the cached handle so ngOnDestroy is triggered.
        // Only detached handles are cached, so an attached route can never be destroyed here.
        try {
          const handle = cacheEntryToRemove[1].handle as any
          // Angular stores the componentRef on the handle in most implementations
          const componentRef =
            handle?.componentRef ?? handle?.route?.componentRef ?? handle?.['componentRef']

          // If the handle exposes a detach() or similar, call it first (framework dependent)
          if (typeof handle?.destroy === 'function') {
            handle.destroy()
          } else if (componentRef) {
            // Detach from app and destroy to trigger ngOnDestroy in the whole tree
            componentRef.hostView?.detach?.()
            componentRef.destroy()
          }
        } catch (e) {
          console.warn(`Cannot destroy cached component`, e)
        }
      }
    }
  }
}

export const provideCustomRouteReuseStrategy = (): Provider[] => {
  return [{ provide: RouteReuseStrategy, useClass: CustomRouteReuseStrategy }]
}
